<template>
  <div class="kpt-detail-content" style="padding: 12px">
    <div class="kpt-detail-content-actions">
      <div class="buttons">
        <b-button type="is-primary is-outlined" icon-left="upload" size="is-small" v-if="!addingVersionVisible" :disabled="isAddingVersion" @click="showAddVersion">Add Version</b-button>
        <b-button type="is-primary is-outlined" icon-left="upload" size="is-small" v-if="addingVersionVisible" :disabled="isAddingVersion || !canSubmit" @click="addSoftwareVersion">Add Software Version</b-button>
        <b-button type="is-primary is-outlined" icon-left="upload" size="is-small" v-if="addingVersionVisible" :disabled="isAddingVersion" @click="hideAddVersion">Cancel</b-button>
      </div>
    </div>
    <div class="kpt-add-software-grid" v-if="addingVersionVisible">
      <b-loading :is-full-page="false" v-model="isAddingVersion" :can-cancel="false"></b-loading>
      <div>
        <b-field grouped>
          <b-field label="Software Version #">
            <b-input v-model="model.version" placeholder="Please select a file below..."></b-input>
          </b-field>
          <b-field label="Platform" required>
            <b-select v-model="model.platform" placeholder="Select a platform" required>
              <option value="ACU">ACU</option>
              <option value="TRCB">TRCB</option>
            </b-select>
          </b-field>
        </b-field>
        <b-field label="Binary" required>
          <b-upload v-model="binaryToUpload" drag-drop expanded>
            <section class="section">
              <div class="content has-text-centered">
                <p><b-icon icon="upload" size="is-large"></b-icon></p>
                <p v-if="!binaryToUpload">Drop your files here or click to upload</p>
                <p v-else>{{binaryToUpload.name}}</p>
              </div>
            </section>
          </b-upload>
        </b-field>
        <b-field label="Minimum Required Version" :type="isMinimumVersionGreaterThanVersion ? 'is-danger' : null" :message="isMinimumVersionGreaterThanVersion ? 'Minimum Version can not be greater than Software Version ' : null" required>
          <b-input v-model="model.minimumRequiredVersion" @input="validateMinimumReqVersion" maxlength="255" :has-counter="false" required></b-input>
        </b-field>
      </div>
      <div>
        <b-field label="Release Date" required>
          <b-datepicker inline v-model="model.releaseDate"></b-datepicker>
        </b-field>
      </div>
    </div>
    <div v-if="versionUnderEdit">
      <b-notification :closable="false" type="is-info">Now editing version: {{versionUnderEdit.version}}</b-notification>
      <div class="kpt-add-software-grid">
        <div>
        <b-field grouped>
          <b-field label="Software Version #">
            <b-input v-model="versionUnderEdit.version" placeholder="Please select a file below..."></b-input>
          </b-field>
          <b-field label="Platform" required>
            <b-select v-model="versionUnderEdit.platform" placeholder="Select a platform" required>
              <option value="ACU">ACU</option>
              <option value="TRCB">TRCB</option>
            </b-select>
          </b-field>
        </b-field>
        <b-field label="Binary" required>
          <b-upload v-model="binaryUnderEdit" drag-drop expanded>
            <b-notification type="is-light" :closable="false">Current binary file: {{getFileName(versionUnderEdit.binaryUri)}}</b-notification>
            <section class="section">
              <div class="content has-text-centered">
                <p><b-icon icon="upload" size="is-large"></b-icon></p>
                <p v-if="!binaryUnderEdit">Drop your files here or click to upload</p>
                <p v-else>{{binaryUnderEdit.name}}</p>
              </div>
            </section>
          </b-upload>
        </b-field>
        <b-field label="Minimum Required Version" 
          :type="isMinimumVersionGreaterThanVersion ? 'is-danger' : null" 
          :message="isMinimumVersionGreaterThanVersion ? 'Minimum Version can not be greater than Software Version ' : null" 
          required>
          <b-input 
            v-model="versionUnderEdit.minimumRequiredVersion" 
            @input="validateMinimumReqVersion" 
            maxlength="255" 
            :has-counter="false" 
            required>
          </b-input>
        </b-field>
      </div>
      <div>
        <b-field label="Release Date" required>
          <b-datepicker inline v-model="versionUnderEdit.releaseDate"></b-datepicker>
        </b-field>
      </div>
      </div>
      <div class="buttons mt-3">
          <b-button type="is-primary" @click="saveVersionUnderEdit" :loading="isUploading" :disabled="isUploading">Save</b-button>
          <b-button @click="versionUnderEdit = null">Cancel</b-button>
        </div>
    </div>
    <div v-else>
      <b-table
        :data="softwareVersionsFlat"
        paginated
        per-page="25"
        :current-page.sync="currentPage"
        default-sort-direction="desc"
        default-sort="releaseDate"
        :striped="true">
        
        <b-table-column width="30" v-slot="props">
            <b-icon size="is-small" class="has-text-primary pointer" icon="edit" @click.native="editVersion(props.row)"></b-icon>
        </b-table-column>

        <b-table-column field="version" label="Software Version" v-slot="props" width="250" sortable>
            {{ props.row.version }}
        </b-table-column>

        <b-table-column field="platform" label="Platform" v-slot="props" width="250" sortable>
            {{ props.row.platform }}
        </b-table-column>

        <b-table-column field="minimumRequiredVersion" label="Minimum Req'd Version" v-slot="props" width="250" sortable>
            {{ props.row.minimumRequiredVersion }}
        </b-table-column>

        <b-table-column field="releaseDate" label="Release Date" v-slot="props" width="200" sortable>
            {{ format(new Date(props.row.releaseDate), 'yyyy-MM-dd') }}
        </b-table-column>

        <b-table-column field="binaryUri" v-slot="props" label="Binary">
            {{ getFileName(props.row.binaryUri) }}
        </b-table-column>

        <template slot="empty">
            <section class="section">
                <div class="content has-text-grey has-text-centered">
                    <p>Nothing here.</p>
                </div>
            </section>
        </template>
      </b-table>
    </div>
  </div>
</template>
<script>
import { format } from 'date-fns';

export default {
  data() {
    return {
      addingVersionVisible: false,
      isAddingVersion: false,
      binaryToUpload: null,
      model: {
        version: '1.0.0',
        minimumRequiredVersion: '',
        platform: 'ACU',
        releaseDate: null
      },
      filterVersion: '',
      isUploading: false,
      currentPage: 1,
      isMinimumVersionGreaterThanVersion: false,
      format,
      versionUnderEdit: null,
      binaryUnderEdit: null,
      isEditingVersion: false
    }
  },
  methods: {
    showAddVersion() {
      this.addingVersionVisible = true;
      this.versionUnderEdit = null;
    },
    hideAddVersion() {
      this.addingVersionVisible = false;
      this.model = {
        version: '1.0.0',
        minimumRequiredVersion: '',
        platform: 'ACU',
        releaseDate: null
      }
    },
    validateMinimumReqVersion() {
      if (!this.model.version) return;
      this.isMinimumVersionGreaterThanVersion = false;
      // compare version with minimum required version (min version must be lower than version)
      const compareMinResult = this.versionCompare(this.model.version.trim(), this.model.minimumRequiredVersion.trim(), {
        lexicographical: true,
        zeroExtend: true
      });
      if (compareMinResult < 0) this.isMinimumVersionGreaterThanVersion = true;
    },
    async addSoftwareVersion() {
      if (!this.canSubmit) return;
      if (this.isAddingVersion) return; // prevent spammers!

      const existingSoftwareVersion = this.softwareVersionsFlat.find(s => s.version == this.model.version);
      const version = this.model.version.trim();

      // break if a conflict was found
      if (existingSoftwareVersion) {
        this.$buefy.toast.open({
          duration: 3000,
          message: `The Software Version '${version}' already exists.`,
          type: 'is-danger'
        });
        return;
      }
      
      const formData = new FormData();
      formData.append('version', version);
      formData.append('minimumRequiredVersion', this.model.minimumRequiredVersion.trim());
      formData.append('platform', this.model.platform.trim());
      formData.append('releaseDate', this.model.releaseDate.toISOString());
      if (this.binaryToUpload.size <= 500000000) { 
        formData.append('files', this.binaryToUpload, this.binaryToUpload.name);
      } else {
        this.$buefy.toast.open({
          duration: 3000,
          message: `File exceeds maximum size limit (500MB).`,
          type: 'is-danger'
        });
        return;
      }

      try {
        this.isAddingVersion = true;
        this.isUploading = true;
        const result = await this.$store.dispatch('addSoftwareVersion', formData);
        if (!result.ok) {
          this.$buefy.toast.open({
            duration: 3000,
            message: `'${await result.text()}', FileName: '${this.binaryToUpload.name}'`,
            type: 'is-danger'
          });
          return;
        }
        this.addingVersionVisible = false;
      } catch (ex) {
        this.$buefy.toast.open({
          duration: 3000,
          message: `There was a problem uploading the software binary: ${ex.message}`,
          type: 'is-danger'
        });
        return;
      } finally {
        // reset display disrupts
        this.isUploading = false;
        this.isAddingVersion = false;
      }

      // reset form inputs
      this.binaryToUpload = null;
      this.model = {
        version: '1.0.0',
        minimumRequiredVersion: '',
        platform: 'ACU',
        releaseDate: null
      }
    },
    async saveVersionUnderEdit() {
      if (this.isEditingVersion) return;

      const existingSoftwareVersion = this.softwareVersionsFlat.find(s => s.version == this.versionUnderEdit.version);
      const version = this.versionUnderEdit.version.trim();

      // break if a conflict was found
      if (existingSoftwareVersion) {
        this.$buefy.toast.open({
          duration: 3000,
          message: `The Software Version '${version}' already exists.`,
          type: 'is-danger'
        });
        return;
      }
      
      const formData = new FormData();
      formData.append('version', version);
      formData.append('minimumRequiredVersion', this.versionUnderEdit.minimumRequiredVersion.trim());
      formData.append('platform', this.versionUnderEdit.platform.trim());
      formData.append('releaseDate', this.versionUnderEdit.releaseDate.toISOString());
      if (this.binaryUnderEdit) {
        if (this.binaryUnderEdit.size <= 200000000) { 
          formData.append('files', this.binaryUnderEdit, this.binaryUnderEdit.name);
        } else {
          this.$buefy.toast.open({
            duration: 3000,
            message: `File exceeds maximum size limit (200MB).`,
            type: 'is-danger'
          });
          return;
        }
      }

      try {
        this.isEditingVersion = true;
        this.isUploading = true;
        const result = await this.$store.dispatch('editSoftwareVersion', { id: this.versionUnderEdit.id, form: formData });
        if (!result.ok) {
          this.$buefy.toast.open({
            duration: 3000,
            message: `'${await result.text()}', FileName: '${this.binaryUnderEdit?.name}'`,
            type: 'is-danger'
          });
          this.isUploading = false;
          return;
        }
        this.isUploading = false;
        this.versionUnderEdit = null;
        this.binaryUnderEdit = null;
      } catch (ex) {
        this.$buefy.toast.open({
          duration: 3000,
          message: `There was a problem uploading the software binary: ${ex.message}`,
          type: 'is-danger'
        });
      } finally {
        // reset display disrupts
        this.isUploading = false;
        this.binaryUnderEdit = null;
        this.versionUnderEdit = null;
        this.isEditingVersion = false;
      }
    },
    getFileName(uri) {
      if (!uri) return null;
      const uriParts = uri.split('/');
      return uriParts[uriParts.length - 1];
    },
    editVersion(version) {
      this.addingVersionVisible = false;
      this.versionUnderEdit = { ...version, releaseDate: new Date(version.releaseDate) };
    },
    // Used by the file upload component
    versionCompare(v1, v2, options) {     
      // short circuit if v2 is null
      if (!v2) return 1;
      const lexicographical = options && options.lexicographical;
      const zeroExtend = options && options.zeroExtend;
      let v1parts = v1.split('.');
      let v2parts = v2.split('.');

      // ensure valid characters
      const isValidPart = (x) => (lexicographical ? /^\d+[A-Za-z]*$/ : /^\d+$/).test(x);

      // ensure both versions meet criteria
      if (!v1parts.every(isValidPart) || !v2parts.every(isValidPart)) {
        return NaN;
      }

      // pad with zeros if necessary when comparing segments of different lengths
      if (zeroExtend) {
        while (v1parts.length < v2parts.length) v1parts.push("0");
        while (v2parts.length < v1parts.length) v2parts.push("0");
      }

      // convert segments including alpha to numerical equivalents
      if (!lexicographical) {
        v1parts = v1parts.map(Number);
        v2parts = v2parts.map(Number);
      }

      // iterate through segment parts
      for (let i = 0; i < v1parts.length; ++i) {
        if (v2parts.length == i) {
          return 1;
        }

        // eslint-disable-next-line no-continue, radix
        if (parseInt(v1parts[i]) == parseInt(v2parts[i])) continue;
        // eslint-disable-next-line radix
        else if (parseInt(v1parts[i]) > parseInt(v2parts[i])) return 1;
        else return -1;
      }
 
      if (v1parts.length != v2parts.length) {
        return -1;
      }

      return 0;
    },
  },
  computed: {
    softwareVersionsFlat() {
      if (!this.filterVersion) return this.softwareVersions;
      const result = this.softwareVersions.filter(sv => sv.version.includes(this.filterVersion));
      return result;
    },
    softwareVersions() {
      return this.$store.state.devices.versions;
    },
    canSubmit() {
      // compare version with minimum required version (min version must be lower than version)
      const compareMinResult = this.versionCompare(this.model.version.trim(), this.model.minimumRequiredVersion.trim(), {
        lexicographical: true,
        zeroExtend: true
      });
      // minimum version must not be higher than the new software version
      if (compareMinResult < 0) return false;

      return (this.model.version && this.model.version.trim())
        && (this.model.minimumRequiredVersion.trim()) 
        && (this.model.releaseDate) 
        && (this.model.platform)
        && (this.binaryToUpload);
    }
  }
}
</script>
<style lang="scss" scoped>
@import '../styles/mixins';

.kpt-add-software-grid {
  position: relative;
  @include display-grid;
  grid-template-columns: 300px 326px;
  gap: 12px;
}
</style>
